import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';

const Page = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Header = styled.h1`
  color: var(--text);
  margin-bottom: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid var(--border);
  color: var(--muted);
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px dashed var(--border);
  color: var(--text);
`;

const SmallBtn = styled.button`
  background: transparent;
  color: var(--accent);
  border: 1px solid var(--accent);
  padding: 2px 8px;
  border-radius: 6px;
  cursor: pointer;
  margin-left: 6px;
`;

const CheckoutBar = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PrimaryBtn = styled.button`
  padding: 12px 20px;
  font-weight: 700;
  border-radius: 24px;
  border: none;
  background: var(--accent);
  color: var(--accent-contrast);
  cursor: pointer;
`;

const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

const Cart = () => {
  const { items, removeItem, updateQty, clearCart, totalCents } = useCart();
  const [loading, setLoading] = useState(false);
  const [publishableKey, setPublishableKey] = useState('');
  const [error, setError] = useState('');
  const tableRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    fetch('/api/config')
      .then((r) => r.json())
      .then((d) => setPublishableKey(d.publishableKey || ''))
      .catch(() => setPublishableKey(''));
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (tableRef.current) gsap.from(tableRef.current, { autoAlpha: 0, y: 14, duration: 1, ease: 'power2.out' });
      if (barRef.current) gsap.from(barRef.current.children, { autoAlpha: 0, y: 8, duration: 1, ease: 'power2.out', stagger: 0.05, delay: 0.1 });
    });
    return () => ctx.revert();
  }, [items.length]);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    setError('');
    try {
      if (!publishableKey) throw new Error('Checkout unavailable. Please try later.');
      if (!window.Stripe) throw new Error('Stripe.js not loaded');
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((i) => ({ name: i.name, quantity: i.quantity, unit_amount: i.unitAmount })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to start checkout');
      const stripe = window.Stripe(publishableKey);
      const result = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      if (result?.error) throw new Error(result.error.message || 'Checkout failed');
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <Page>
      <Header>Your Cart</Header>
      {error ? (
        <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>
      ) : null}
      {items.length === 0 ? (
        <div style={{ color: '#fff' }}>
          Your cart is empty. <Link to="/products">Browse Products</Link>
        </div>
      ) : (
        <>
          <Table ref={tableRef}>
            <thead>
              <tr>
                <Th>Item</Th>
                <Th>Qty</Th>
                <Th>Price</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id}>
                  <Td>{i.name}</Td>
                  <Td>
                    <SmallBtn onClick={() => updateQty(i.id, i.quantity - 1)}>-</SmallBtn>
                    <span style={{ margin: '0 8px' }}>{i.quantity}</span>
                    <SmallBtn onClick={() => updateQty(i.id, i.quantity + 1)}>+</SmallBtn>
                  </Td>
                  <Td>{formatCents(i.unitAmount * i.quantity)}</Td>
                  <Td>
                    <SmallBtn onClick={() => removeItem(i.id)}>Remove</SmallBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <CheckoutBar ref={barRef}>
            <div style={{ color: 'var(--text)' }}>Total: {formatCents(totalCents)}</div>
            <div>
              <SmallBtn onClick={clearCart}>Clear</SmallBtn>
              <PrimaryBtn style={{ marginLeft: 12 }} disabled={loading || !publishableKey} onClick={checkout}>
                {loading ? 'Redirecting…' : 'Checkout'}
              </PrimaryBtn>
            </div>
          </CheckoutBar>
        </>
      )}
    </Page>
  );
};

export default Cart;
